import { useCallback, useMemo } from 'react'

import { usePublicClient } from 'wagmi'
import { privateKeyToAccount } from 'viem/accounts'

import { Hash } from 'types/entities'

import { useWaitTx } from 'hooks/wallet'

import { AppState } from 'state'
import { useAirdropId } from 'state/airdrop/hooks'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import { isEmpty } from 'utils'
import { TxStatuses, TxTypes } from 'constants/transaction'

import { Setting, Settings, TransactionsHistory, Transaction } from './types'
import { updateSetting, setPrivateKey, userClearState, updateTransaction, setIsPKSaved, clearPKWallet } from './slice'

function getTxsByStatus(txs: TransactionsHistory, status: TxStatuses) {
  return Object.values(txs).reduce<TransactionsHistory>((acc, curr) => {
    if (curr.status === status) {
      acc[curr.txHash] = curr
    }
    return acc
  }, {})
}

function getTxsByType(txs: TransactionsHistory, types: TxTypes[], airdropId?: string | null) {
  return Object.entries(txs).reduce<TransactionsHistory>((acc, [currKey, currValue]) => {
    if (types.includes(currValue.type) && (!airdropId || currValue.airdropId === airdropId)) {
      acc[currKey as Hash] = currValue
    }
    return acc
  }, {})
}

// action creator
export function useUpdateTransactionAndWait() {
  const publicClient = usePublicClient()
  const waitTx = useWaitTx()
  const onUpdateTx = useUpdateTransactionHistory()

  return useCallback(
    async (tx: Omit<Transaction, 'status'> & { status?: TxStatuses }): Promise<Transaction | null> => {
      try {
        const waitedTx = await waitTx(tx)
        onUpdateTx(waitedTx)

        return waitedTx
      } catch (err) {
        return null
      }
    },
    [publicClient, onUpdateTx],
  )
}

// handlers
export function usePendingTransactions() {
  const transactionsHistory = useTransactionsHistory()
  return useMemo(() => getTxsByStatus(transactionsHistory, TxStatuses.PENDING), [transactionsHistory])
}

export function useIsPendingApproveTxs() {
  const pendingTransactions = usePendingTransactions()

  const pendingApproveTxs = getTxsByType(pendingTransactions, [TxTypes.REVOKE, TxTypes.APPROVE])

  return !isEmpty(pendingApproveTxs)
}

export function useIsPendingAirdropTxs() {
  const airdropId = useAirdropId()
  const pendingTransactions = usePendingTransactions()

  if (!airdropId) {
    return false
  }

  const pendingAirdropTxs = getTxsByType(
    pendingTransactions,
    [TxTypes.AIRDROP_WITH_SIGNATURE, TxTypes.AIRDROP],
    airdropId,
  )

  return !isEmpty(pendingAirdropTxs)
}

export function useIsPendingRefundTx() {
  const pendingTransactions = usePendingTransactions()

  const pendingRefundTxs = getTxsByType(pendingTransactions, [TxTypes.REFUND])

  return !isEmpty(pendingRefundTxs)
}

export function useIsPendingTopUpTx() {
  const airdropId = useAirdropId()
  const pendingTransactions = usePendingTransactions()

  if (!airdropId) {
    return false
  }

  const pendingTopUpTxs = getTxsByType(pendingTransactions, [TxTypes.TOP_UP_SELF_GENERATED], airdropId)

  return !isEmpty(pendingTopUpTxs)
}

// actions
export function useClearState() {
  const dispatch = useAppDispatch()
  return useCallback(() => {
    dispatch(userClearState())
  }, [dispatch])
}

export function useUpdateSetting() {
  const dispatch = useAppDispatch()

  return useCallback(
    (key: keyof typeof Setting, value: Settings[keyof typeof Setting]) => {
      dispatch(updateSetting({ key, value }))
    },
    [dispatch],
  )
}

export function useSetPrivateKey() {
  const dispatch = useAppDispatch()

  return useCallback(
    (privateKey: string | null) => {
      dispatch(setPrivateKey(privateKey))
    },
    [dispatch],
  )
}

export function useSetIsPKSaved() {
  const dispatch = useAppDispatch()

  return useCallback(() => {
    dispatch(setIsPKSaved())
  }, [dispatch])
}

export function useClearPKWallet() {
  const dispatch = useAppDispatch()

  return useCallback(() => {
    dispatch(clearPKWallet())
  }, [dispatch])
}

// handlers
export function useUpdateTransactionHistory() {
  const dispatch = useAppDispatch()

  return useCallback(
    (transaction: Transaction) => {
      dispatch(updateTransaction(transaction))
    },
    [dispatch],
  )
}

export function useSelfGeneratedAddress() {
  const privateKey = usePrivateKey()

  return useMemo(() => {
    if (!privateKey) {
      return null
    }
    const account = privateKeyToAccount(privateKey)
    return account.address
  }, [privateKey])
}

// selectors
export function useTransactionsHistory() {
  return useAppSelector((state: AppState) => state.user.transactionsHistory)
}

// setting
export function useSettings() {
  return useAppSelector((state: AppState) => state.user.settings)
}

export function useSettingRpcUrl() {
  return useAppSelector((state: AppState) => state.user.settings[Setting.RPC_URL])
}

export function useIsMaxApprove() {
  return useAppSelector((state: AppState) => state.user.settings[Setting.IS_MAX_APPROVE])
}

export function useShowAddressGenerator() {
  return useAppSelector((state: AppState) => state.user.settings[Setting.SHOW_ADDRESS_GENERATOR])
}

export function usePrivateKey() {
  return useAppSelector((state: AppState) => state.user.privateKey)
}

export function useIsPKSaved() {
  return useAppSelector((state: AppState) => state.user.isPKSaved)
}
